import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./LandingPage.module.css";

const FONT_LINK_ID = "clientkit-landing-fonts";

function useLandingFonts() {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;

    const preconnect1 = document.createElement("link");
    preconnect1.rel = "preconnect";
    preconnect1.href = "https://fonts.googleapis.com";
    preconnect1.dataset.landingFont = "true";

    const preconnect2 = document.createElement("link");
    preconnect2.rel = "preconnect";
    preconnect2.href = "https://fonts.gstatic.com";
    preconnect2.crossOrigin = "anonymous";
    preconnect2.dataset.landingFont = "true";

    const fonts = document.createElement("link");
    fonts.id = FONT_LINK_ID;
    fonts.rel = "stylesheet";
    fonts.href =
      "https://fonts.googleapis.com/css2?family=Anton&family=IBM+Plex+Mono:wght@300;400;500&family=Fraunces:opsz,ital,wght@9..144,0,400;9..144,0,600;9..144,1,400&display=swap";

    document.head.appendChild(preconnect1);
    document.head.appendChild(preconnect2);
    document.head.appendChild(fonts);
  }, []);
}

function useScrollScrub(sentinelRef, videoRef) {
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const video = videoRef.current;
    if (!sentinel || !video) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    let lastProgress = -1;
    let ticking = false;

    const update = () => {
      const dur = video.duration;
      if (!Number.isFinite(dur) || dur <= 0) return;

      const rect = sentinel.getBoundingClientRect();
      const total = sentinel.offsetHeight;
      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / total));

      if (Math.abs(progress - lastProgress) < 0.001) return;
      lastProgress = progress;

      const target = progress * dur;
      if (Math.abs(video.currentTime - target) > 0.01) {
        video.currentTime = target;
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    const start = () => {
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      update();
    };

    let cleanupReady = null;
    if (video.readyState >= 1) {
      start();
    } else {
      cleanupReady = () => video.removeEventListener("loadedmetadata", start);
      video.addEventListener("loadedmetadata", start, { once: true });
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (cleanupReady) cleanupReady();
    };
  }, [sentinelRef, videoRef]);
}

export default function LandingPage() {
  const navigate = useNavigate();
  const sentinelRef = useRef(null);
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);

  useLandingFonts();
  useScrollScrub(sentinelRef, videoRef);

  const goSignIn = (e) => {
    e.preventDefault();
    navigate("/sign-in");
  };
  const goSignUp = (e) => {
    e.preventDefault();
    navigate("/sign-up");
  };

  return (
    <div className={styles.root}>
      <main>
        {/* Static intro — H1 owns first paint. Scroll-scrub morph below = act II,
            not layered. v0.2 Plan 06-03 fix: prior overlay stacked H1 on top of
            the morphing wordmark and read as visual chaos. */}
        <header className={styles.heroIntro}>
          <nav className={styles.heroNav} aria-label="Primary">
            <span className={styles.navMark}>ClientKit<span className={styles.navMarkSub}> · freelance OS</span></span>
            <ul className={styles.navLinks}>
              <li>
                <a href="#firewall">features</a>
              </li>
              <li>
                <a
                  href="/sign-in"
                  onClick={goSignIn}
                  className={styles.navLogin}
                >
                  log in
                </a>
              </li>
              <li>
                <a
                  href="/sign-up"
                  onClick={goSignUp}
                  className={styles.navCta}
                >
                  start free
                </a>
              </li>
            </ul>
          </nav>

          <div className={styles.heroCopy}>
            <h1 className={styles.heroHeadline}>
              Stop dreading
              <br />
              revision emails.
            </h1>
            <p className={styles.heroSubhead} data-testid="hero-subhead">
              For designers tired of revision creep and brief changes.
            </p>
          </div>

          <a href="#firewall" className={styles.heroIntroCue} aria-label="Scroll to see how it works">
            <span className={styles.heroIntroCueLine} aria-hidden="true" />
            <span className={styles.heroIntroCueText}>scroll</span>
          </a>
        </header>

        <div className={styles.scrubCage}>
          <div
            ref={sentinelRef}
            className={styles.scrubSentinel}
            aria-hidden="true"
          />
          <section className={styles.hero}>
            <div
              className={styles.heroMedia}
              aria-label="ClientKit wordmark resolving from chaotic client-work typography"
            >
              <video
                ref={videoRef}
                className={styles.heroVideo}
                muted
                playsInline
                preload="metadata"
                poster="/landing/hero-end.webp"
                onError={() => setVideoFailed(true)}
                aria-hidden="true"
              >
                <source src="/landing/hero-morph.mp4" type="video/mp4" />
              </video>
              <img
                className={`${styles.heroFallback} ${videoFailed ? styles.heroFallbackForce : ""}`}
                src="/landing/hero-end.webp"
                alt=""
              />
            </div>

            <div className={styles.heroCue} aria-hidden="true">
              <span className={styles.cueLine} />
              <span className={styles.cueText}>
                scroll to see the chaos resolve
              </span>
            </div>
          </section>
        </div>

        <section className={styles.firewall} id="firewall">
          <div className={styles.firewallInner}>
            <div>
              <span className={styles.firewallEyebrow}>
                01 — Scope Guard
              </span>
              <h2 className={styles.firewallHeading}>
                Stop dreading
                <br />
                revision creep.
              </h2>
              <p className={styles.firewallBody}>
                ClientKit reads the revision request. You set the policy. You
                ship a response that protects scope and saves the relationship.
              </p>
              <a
                href="/sign-up"
                onClick={goSignUp}
                className={styles.firewallCta}
              >
                try Scope Guard →
              </a>
            </div>

            <div
              className={styles.firewallDemo}
              aria-label="Sample Scope Guard exchange"
            >
              <div className={styles.demoRow}>
                <span className={styles.demoLabel}>Incoming</span>
                <p className={styles.demoMsg}>
                  actually, can we get one more round of revisions on the logo
                  direction? we want to explore the color palette more — should
                  be quick!
                </p>
              </div>
              <div className={styles.demoRow}>
                <span className={styles.demoLabel}>Tone</span>
                <div className={styles.demoTones}>
                  <span className={`${styles.tone} ${styles.toneActive}`}>
                    warm
                  </span>
                  <span className={styles.tone}>firm</span>
                  <span className={styles.tone}>boundary</span>
                  <span className={styles.tone}>brief</span>
                </div>
              </div>
              <div className={`${styles.demoRow} ${styles.demoRowReply}`}>
                <span className={styles.demoLabel}>ClientKit reply</span>
                <p className={styles.demoMsg}>
                  Happy to keep exploring. We're already past the 2 revision
                  rounds in your contract — I'll send over a change order for
                  one more round with the additional fee and timeline so
                  everyone's aligned before I dig in. Sound good?
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.capabilities} aria-labelledby="cap-heading">
          <h2 id="cap-heading" className={styles.capabilitiesHeading}>
            Every other client situation.
            <br />
            Handled.
          </h2>
          <ol className={styles.capabilitiesList}>
            <li>
              <span className={styles.capNum}>02</span>
              <div>
                <h3 className={styles.capTitle}>Change Orders.</h3>
                <p className={styles.capLede}>
                  Every "one quick thing" gets logged, priced, and confirmed
                  before you touch it. No more silent scope creep.
                </p>
              </div>
            </li>
            <li>
              <span className={styles.capNum}>03</span>
              <div>
                <h3 className={styles.capTitle}>
                  Proposals + e-signed contracts.
                </h3>
                <p className={styles.capLede}>
                  Win the work with polished proposals. Close it with e-signed
                  contracts. One tool, not five.
                </p>
              </div>
            </li>
            <li>
              <span className={styles.capNum}>04</span>
              <div>
                <h3 className={styles.capTitle}>
                  Client portal, branded to you.
                </h3>
                <p className={styles.capLede}>
                  Your clients log in to one place — your name on the door.
                  Looks like your studio, runs on ClientKit.
                </p>
              </div>
            </li>
          </ol>
          <p className={styles.capabilitiesAlsoIncludes}>
            Also includes: CRM pipeline · Proof rounds · Invoices that send
            themselves.
          </p>
        </section>

        <section className={styles.who}>
          <p className={styles.whoBody}>
            Freelancing shouldn't feel like a hostage negotiation. ClientKit is
            built for independent designers, freelance developers, two-person
            studios, and consultants who've stopped pretending email is a
            project management system.
          </p>
        </section>

        <section className={styles.closing} id="start">
          <h2 className={styles.closingStatement}>
            Your clients are emailing you right now.
          </h2>
          <p className={styles.closingBody}>
            Let ClientKit handle the hard conversations so you can focus on the
            work you love.
          </p>
          <a
            href="/sign-up"
            onClick={goSignUp}
            className={styles.closingCta}
          >
            start free
          </a>
          <p className={styles.closingMeta}>
            Free forever. Pro from $19/mo. No card to start.
          </p>
        </section>

        <footer className={styles.siteFoot}>
          <span>ClientKit · freelance OS</span>
          <div className={styles.siteFootLinks}>
            <Link to="/privacy" className={styles.siteFootLink}>privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className={styles.siteFootLink}>terms</Link>
          </div>
          <span>2026 — built for people who'd rather be doing the work</span>
        </footer>
      </main>
    </div>
  );
}
